import React, { useCallback, useEffect, useMemo, useState } from 'react';
import TopNavbar from '../../components/TopNavBar/TopNavBar';
import ProductConfirmation from '../../components/ProductConfirmation/ProductConfirmation';
import OrderSummary from '../../components/OrderSummary/OrderSummary';
import PaymentConfirmation from '../../components/PaymentConfirmation/PaymentConfirmation';
import OrderDetailsDocuments from '../../components/OrderDetailsDocuments/OrderDetailsDocuments';
import OrderDocumentsList from '../../components/OrderDocumentsList/OrderDocumentsList';
import AdminPortalSideBar from '../../containers/AdminSideBarContainer/AdminSideBarContainer';

import {
  PageContainer,
  ContentContainer,
  VerticalPaddingContainer,
  SectionCard,
  SectionTitle
} from './OrderStatusFinal.styles';

import {
  Box,
  Button,
  Divider,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from '@mui/material';
import OrderPreviewModal from '../../components/OrderPreviewModal/OrderPreviewModal';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CancelIcon from '@mui/icons-material/Cancel';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import InputAdornment from '@mui/material/InputAdornment';

import {
  updateOrderStatus,
  getDashboardUser,
  getProfile,
  getOrderById,
  confirmOrderAndSendEmails,
  getOrderStatusHistory,
  cancelOrder,
  applyOrderCredit,
  validateOrder,
  getOrderShipments,
  addInternalNote,
} from '../../api/api';

import {useToast} from '../../components/ToastProvider/ToastProvider';
import { useAuth } from '../../contexts/AuthContext';

const STATUS = ['AWAITING VENDOR CONFIRMATION', 'VENDOR VALIDATION REQUESTED', 'VENDOR CONFIRMED', 'AWAITING PAYMENT', 'PAID', 'VENDOR ORDER PROCESSED', 'ASSEMBLING ORDER', 'SHIPPED', 'DELIVERED'];
const BASE_STATUSES = ['AWAITING VENDOR CONFIRMATION', 'VENDOR VALIDATION REQUESTED', 'VENDOR CONFIRMED', 'AWAITING PAYMENT', 'PAID', 'VENDOR ORDER PROCESSED', 'ASSEMBLING ORDER', 'SHIPPED', 'DELIVERED'];

const CANCELLATION_REASONS = [
  'Customer requested cancellation',
  'Incorrect product ordered',
  'Duplicate order placed',
  'Order placed by mistake',
  'Customer failed to make payment',
  'Product out of stock',
  'Vendor unable to fulfill the order',
  'Vendor confirmation not received',
  'Product discontinued',
  'Payment verification failed',
  'Invalid or incomplete address'
];

const num = (v) => Number(v || 0);
const round2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;

export default function OrderStatusFinal({ embedded = false, order = null, onOrderChanged, readOnly = false }) {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canViewOrders = hasPermission('orders.view') || hasPermission('orders.manage');
  const [internalNote, setInternalNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Initialize level from order data (persisted from previous sessions)
  // level >= 1: Order Summary enabled (if order_summary_enabled_at is set)
  // level >= 2: Payment Confirmation & Documents enabled (if confirmation_first_sent_at is set)
  const getInitialLevel = () => {
    if (!order) return 0;
    if (order.confirmation_first_sent_at) return 2;  // Emails sent = level 2
    if (order.order_summary_enabled_at) return 1;    // UPDATE clicked = level 1
    return 0;
  };
  
  const [level, setLevel] = useState(getInitialLevel());
  const [status, setStatus] = useState(
    order?.status && STATUS.includes(order.status) ? order.status : 'AWAITING VENDOR CONFIRMATION'
  );
  const [notes, setNotes] = useState('');
  const [displayName, setDisplayName] = useState('user');
  const [confirming, setConfirming] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [prevBeforeCancel, setPrevBeforeCancel] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [customCancelReason, setCustomCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancellationReason, setCancellationReason] = useState(null);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [creditAmount, setCreditAmount] = useState('');
  const [applyingCredit, setApplyingCredit] = useState(false);
  const [summaryBillable, setSummaryBillable] = useState(null);
  const handleBillableChange = useCallback((v) => {
    setSummaryBillable(Number.isFinite(v) ? v : null);
  }, []);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [statusHistory, setStatusHistory] = useState([]);
  const [shipments, setShipments] = useState([]);
  
  // Track original order state to detect changes
  const [originalOrderSnapshot, setOriginalOrderSnapshot] = useState(null);
  const [currentOrder, setCurrentOrder] = useState(order);

  const effectiveOrderId = useMemo(() => {
    const fromProp =
      order?.id ??
      order?.order_id ??
      (typeof order?.orderNumber === 'string'
        ? Number(order.orderNumber.replace(/\D/g, '')) || undefined
        : undefined);

    if (fromProp) return fromProp;

    if (typeof window !== 'undefined') {
      const m = window.location.pathname.match(/\/(?:orders|user-order-management)\/(\d+)/);
      if (m && m[1]) return Number(m[1]);
    }
    return undefined;
  }, [order]);

  useEffect(() => {
    (async () => {
      try {
        const u = await getDashboardUser();
        let nice = u?.user?.username || 'user';
        try {
          const p = await getProfile();
          if (p?.full_name) nice = p.full_name;
        } catch {}
        setDisplayName(nice);
      } catch {
        setDisplayName('user');
      }
    })();
  }, []);

  useEffect(() => {
    if (order?.status) {
      setStatus(STATUS.includes(order.status) ? order.status : 'AWAITING VENDOR CONFIRMATION');
      // Snapshot original order when it first loads
      if (!originalOrderSnapshot && order?.items) {
        setOriginalOrderSnapshot({
          status: order.status,
          items: order.items.map(item => ({
            id: item.id,
            quantity: item.quantity,
            unit_price: item.unit_price || item.product_price,
            availability: item.availability || 'available'
          }))
        });
      }
      setCurrentOrder(order);
      
      // Update level based on order state
      if (order.confirmation_first_sent_at) {
        setLevel(2);  // Emails sent = level 2
      } else if (order.order_summary_enabled_at) {
        setLevel(1);  // UPDATE clicked = level 1
      }
      
      // If order is cancelled, check for cancellation_reason in order object first (fast)
      if (order.status === 'CANCELLED' && order.cancellation_reason && !cancellationReason) {
        setCancellationReason(order.cancellation_reason);
      }
    }
  }, [order]);

  // When cancelled, fetch cancellation reason from status-history (same logic as mail template)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Check if order is cancelled from multiple sources
        const orderStatus = status || currentOrder?.status || order?.status;
        
        console.log('🔍 Cancellation reason useEffect triggered:', {
          status,
          'currentOrder?.status': currentOrder?.status,
          'order?.status': order?.status,
          orderStatus,
          effectiveOrderId,
          cancellationReason
        });
        
        // If not cancelled or no order ID, return early
        if (orderStatus !== 'CANCELLED' || !effectiveOrderId) {
          if (alive && orderStatus !== 'CANCELLED') {
            setPrevBeforeCancel(null);
          }
          return;
        }
        
        // PRIORITY 1: Check order.cancellation_reason (stored directly in orders table - fastest)
        const orderCancellationReason = order?.cancellation_reason || currentOrder?.cancellation_reason;
        if (orderCancellationReason && alive) {
          console.log('✅ Using cancellation_reason from order object:', orderCancellationReason);
          setCancellationReason(orderCancellationReason);
          // Still need to set prevBeforeCancel, so continue to fetch history
        }
        
        // Skip history fetch if reason already set (prevents duplicate API calls)
        if (cancellationReason || orderCancellationReason) {
          console.log('⏭️ Cancellation reason already set, skipping history fetch');
          // But still fetch prevBeforeCancel if needed
        }
        
        // PRIORITY 2: Fallback to parsing from status_history (for older orders or if order.cancellation_reason not available)
        // Only fetch if reason is not already set from order object
        if (!orderCancellationReason) {
          // Helper function to extract reason from notes - EXACTLY like backend stores it
          // Backend format: "Order cancelled. Reason: {cancellation_reason}"
          // Example from Status History: "Order cancelled. Reason: Duplicate order placed"
          const extractReason = (notes) => {
            if (!notes) return null;
            // Try multiple patterns to be safe
            // Pattern 1: "Order cancelled. Reason: {reason}" - exact match
            // Pattern 2: "Reason: {reason}" - fallback
            let reasonMatch = notes.match(/Order cancelled\.\s*Reason:\s*(.+)$/i);
            if (!reasonMatch) {
              reasonMatch = notes.match(/Reason:\s*(.+)$/i);
            }
            if (!reasonMatch) {
              reasonMatch = notes.match(/Reason:\s*(.+?)(?:\.|$)/i);
            }
            if (reasonMatch && reasonMatch[1]) {
              return reasonMatch[1].trim();
            }
            return null;
          };
          
          // Fetch status history from API (same as mail template uses OrderStatusHistory)
          console.log('🔍 Fetching cancellation reason from status_history for order:', effectiveOrderId);
          const history = await getOrderStatusHistory(effectiveOrderId);
          if (!alive) return;
          
          console.log('📋 Status history received:', history);
          
          if (Array.isArray(history) && history.length > 0) {
            // History is ordered oldest first (backend: .order_by(OrderStatusHistory.created_at.asc()))
            // Find ALL cancellation entries and get the LAST one (most recent)
            const cancelEntries = history.filter((h) => h.new_status === 'CANCELLED' && h.notes);
            
            console.log('🚫 Cancellation entries found:', cancelEntries.length, cancelEntries);
            
            if (cancelEntries.length > 0) {
              // Get the last cancellation entry (most recent)
              const lastCancel = cancelEntries[cancelEntries.length - 1];
              
              console.log('📝 Last cancellation entry:', lastCancel);
              
              if (lastCancel?.notes) {
                console.log('📄 Notes to extract from:', lastCancel.notes);
                const reason = extractReason(lastCancel.notes);
                console.log('✅ Extracted reason:', reason);
                if (reason && alive) {
                  setCancellationReason(reason);
                } else {
                  console.warn('⚠️ Could not extract reason from notes:', lastCancel.notes);
                }
              }
              
              // Set prevBeforeCancel if available
              if (lastCancel?.old_status && BASE_STATUSES.includes(lastCancel.old_status) && alive) {
                setPrevBeforeCancel(lastCancel.old_status);
              } else if (alive) {
                setPrevBeforeCancel('AWAITING VENDOR CONFIRMATION');
              }
            } else if (alive) {
              setPrevBeforeCancel('AWAITING VENDOR CONFIRMATION');
            }
          } else if (alive) {
            setPrevBeforeCancel('AWAITING VENDOR CONFIRMATION');
          }
        }
        
        // Always fetch prevBeforeCancel even if reason is already set from order object
        if (orderCancellationReason) {
          console.log('✅ Reason already set from order object, fetching history for prevBeforeCancel only');
          const historyForPrev = await getOrderStatusHistory(effectiveOrderId);
          if (alive && Array.isArray(historyForPrev) && historyForPrev.length > 0) {
            const cancelEntries = historyForPrev.filter((h) => h.new_status === 'CANCELLED');
            if (cancelEntries.length > 0) {
              const lastCancel = cancelEntries[cancelEntries.length - 1];
              if (lastCancel?.old_status && BASE_STATUSES.includes(lastCancel.old_status) && alive) {
                setPrevBeforeCancel(lastCancel.old_status);
              } else if (alive) {
                setPrevBeforeCancel('AWAITING VENDOR CONFIRMATION');
              }
            } else if (alive) {
              setPrevBeforeCancel('AWAITING VENDOR CONFIRMATION');
            }
          } else if (alive) {
            setPrevBeforeCancel('AWAITING VENDOR CONFIRMATION');
          }
        }
      } catch (e) {
        console.error('Error fetching cancellation reason:', e);
        if (alive) {
          setPrevBeforeCancel('AWAITING VENDOR CONFIRMATION');
          // Don't clear cancellationReason if it was already set (e.g., from immediate cancel)
        }
      }
    })();
    return () => { alive = false; };
  }, [status, effectiveOrderId, currentOrder?.status, order?.status, order]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!effectiveOrderId) return;
      if (order?.status) return;
      try {
        const fresh = await getOrderById(effectiveOrderId);
        if (!alive) return;
        if (fresh?.status) setStatus(STATUS.includes(fresh.status) ? fresh.status : 'AWAITING VENDOR CONFIRMATION');
        if (fresh && !order && typeof onOrderChanged === 'function') {
          onOrderChanged(fresh);
        }
      } catch {}
    })();
    return () => { alive = false; };
  }, [effectiveOrderId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if there are any changes (status changes OR note-only updates)
  // UPDATE button should activate for:
  // 1. Status changes
  // 2. Note-only updates (note typed, status unchanged)
  const hasChanges = useMemo(() => {
    if (!originalOrderSnapshot || !currentOrder) return false;
    
    // Check status change
    if (status !== originalOrderSnapshot.status) return true;
    
    // Check note-only update: note is typed and not empty
    if (notes && notes.trim().length > 0) return true;
    
    // No changes
    return false;
  }, [originalOrderSnapshot, currentOrder, status, notes]);

  const handleUpdate = async () => {
    if (!effectiveOrderId || !hasChanges || updating) return;
    
    setUpdating(true);
    try {
      // Prepare items array with current state for backend to detect, save, and log changes
      const itemsArray = (currentOrder?.items || order?.items || []).map(item => ({
        id: item.id,
        quantity: item.quantity,
        unit_price: item.unit_price || item.product_price,
        product_price: item.product_price,
        availability: item.availability || 'available'
      }));
      
      const updated = await updateOrderStatus(effectiveOrderId, { 
        status, 
        notes,
        items: itemsArray
      });
      if (onOrderChanged) onOrderChanged(updated);
      
      // Update snapshot after successful update
      if (updated?.items) {
        setOriginalOrderSnapshot({
          status: updated.status,
          items: updated.items.map(item => ({
            id: item.id,
            quantity: item.quantity,
            unit_price: item.unit_price || item.product_price,
            availability: item.availability || 'available'
          }))
        });
        setStatus(updated.status);
        setCurrentOrder(updated);
      }
      
      // Clear notes after successful update
      setNotes('');
      
      // UPDATE button only enables Order Summary (level 1)
      // Don't set level to 2 here - that happens only on CONFIRM ORDER
      setLevel((prev) => (prev < 1 ? 1 : prev));
    } catch (error) {
      console.error('Update failed:', error);
      showToast(`Update failed: ${error?.message || error}`, 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handlePaymentLinkCreated = () => setLevel(2);

  useEffect(() => {
    const listener = () => setLevel(2);
    window.addEventListener('payment-link-created', listener);
    return () => window.removeEventListener('payment-link-created', listener);
  }, []);

  // ---------- Compute totals ----------
  const computedTotals = useMemo(() => {
    const items = Array.isArray(currentOrder?.items || order?.items) ? (currentOrder?.items || order.items) : [];
    let subtotal = 0;

    if (items.length) {
      // Filter items: only include available items that are not excluded
      // Same logic as OrderSummary component
      const availableItems = items.filter(item => {
        const isAvailable = item.availability === 'available';
        const isIncluded = (item.order_item_status || 'include') !== 'exclude';
        return isAvailable && isIncluded;
      });
      
      subtotal = availableItems.reduce((acc, it) => {
        // Exclude ON_DEMAND items with no price (same logic as OrderSummary)
        const unitPriceNum = Number(it.unit_price ?? it.product_price ?? 0);
        const isOnDemand = (it.pricing_mode === 'ON_DEMAND' && (unitPriceNum === 0 || unitPriceNum === null || unitPriceNum === undefined)) ||
                           (it.pricing_mode !== 'FIXED' && (unitPriceNum === 0 || unitPriceNum === null || unitPriceNum === undefined));
        
        if (isOnDemand) {
          return acc; // Exclude from subtotal
        }
        
        const line =
          num(it.total_price) ||
          (num(it.unit_price ?? it.product_price) * num(it.quantity)) - num(it.discount_amount);
        return acc + num(line);
      }, 0);
    } else {
      subtotal = num(currentOrder?.subtotal || order?.subtotal);
    }

    const shipping = num(currentOrder?.shipping_cost || order?.shipping_cost) || 0;
    
    // Use tax_amount from backend if available (it's already calculated correctly after availability changes)
    // Otherwise calculate based on billing country (same logic as OrderSummary):
    // - If billing country is Mauritius: 15% of (subtotal + shipping)
    // - For all other countries: 0%
    let tax = 0;
    const backendTax = currentOrder?.tax_amount ?? order?.tax_amount;
    if (backendTax !== undefined && backendTax !== null) {
      // Use backend-calculated tax (already includes billing country logic and reflects availability changes)
      tax = round2(num(backendTax));
    } else {
      // Fallback: Calculate tax based on billing country
      const billingCountry = (currentOrder?.billing_country || order?.billing_country || '').toLowerCase();
      if (billingCountry === 'mauritius') {
        tax = round2((subtotal + shipping) * 0.15); // 15% of (subtotal + shipping) for Mauritius
      } else {
        tax = 0; // 0% tax for all other countries
      }
    }
    
    const billable = round2(subtotal + tax + shipping);

    return {
      subtotal: round2(subtotal),
      shipping,
      tax,
      billable,
    };
  }, [currentOrder, order]);

  // derived order object (don't mutate original)
  const computedOrder = useMemo(() => {
    const sourceOrder = currentOrder || order;
    if (!sourceOrder) return sourceOrder;
    return {
      ...sourceOrder,
      subtotal: computedTotals.subtotal,
      shipping_cost: computedTotals.shipping,
      tax_amount: computedTotals.tax,
      total_amount: computedTotals.billable,
    };
  }, [currentOrder, order, computedTotals]);

  const handlePreviewOrder = async () => {
    // Fetch status history to get order changes
    if (effectiveOrderId) {
      try {
        const history = await getOrderStatusHistory(effectiveOrderId);
        setStatusHistory(history || []);
      } catch (e) {
        console.error('Failed to fetch status history:', e);
        setStatusHistory([]);
      }
      
      // Fetch shipments for shipping calculation in preview
      try {
        const shipmentsData = await getOrderShipments(effectiveOrderId);
        setShipments(shipmentsData || []);
      } catch (e) {
        console.error('Failed to fetch shipments:', e);
        setShipments([]);
      }
    }
    setPreviewModalOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewModalOpen(false);
  };

  const handleConfirmOrder = async () => {
    if (!effectiveOrderId || confirming) return;
    try {
      setConfirming(true);
      // Keep modal open to show loading widget
      
      // Validate order first
      await validateOrder(effectiveOrderId);
      
      // After validation, refresh order data to show updated values
      const refreshedOrder = await getOrderById(effectiveOrderId);
      if (onOrderChanged) onOrderChanged(refreshedOrder);
      
      // Existing functionality: Confirm order and send emails
      const updated = await confirmOrderAndSendEmails(effectiveOrderId);
      if (onOrderChanged) onOrderChanged(updated);
      
      // CONFIRM ORDER button enables Payment Confirmation & Documents (level >= 2)
      // Check if confirmation_first_sent_at is set (email was sent)
      if (updated?.confirmation_first_sent_at) {
        setLevel(2);
      } else {
        // Even if email failed, set level 2 to enable sections (already confirmed)
        setLevel(2);
      }
      
      // Show success message
      showToast('Order validated and confirmed successfully', 'success');
      
      // Close modal after success
      setPreviewModalOpen(false);
    } catch (e) {
      console.error('Confirm order failed', e);
      showToast(`Failed to validate order: ${e?.message || e}`, 'error');
    } finally {
      setConfirming(false);
    }
  };

  const handleCancelOrder = async () => {
    // Use either radio-selected reason or custom text reason
    const finalReason = cancelReason || customCancelReason.trim();
    if (!effectiveOrderId || !finalReason || cancelling) return;
    try {
      setCancelling(true);
      const updated = await cancelOrder(effectiveOrderId, finalReason);
      if (onOrderChanged) onOrderChanged(updated);
      
      setStatus('CANCELLED');
      setCurrentOrder(updated);
      setCancellationReason(finalReason); // Set reason immediately so it shows right away
      setCancelDialogOpen(false);
      setCancelReason('');
      setCustomCancelReason('');
      
      showToast('Order cancelled successfully', 'success');
    } catch (e) {
      console.error('Cancel order failed', e);
      showToast(`Failed to cancel order: ${e?.message || e}`, 'error');
    } finally {
      setCancelling(false);
    }
  };

  const handleApplyCredit = async () => {
    const amt = parseFloat(creditAmount);
    if (!effectiveOrderId || isNaN(amt) || amt <= 0 || applyingCredit) return;
    try {
      setApplyingCredit(true);
      const billableForServer =
        summaryBillable !== null && summaryBillable !== undefined
          ? summaryBillable
          : computedTotals?.billable;
      const updated = await applyOrderCredit(effectiveOrderId, amt, billableForServer);
      if (onOrderChanged) onOrderChanged(updated);
      setCurrentOrder(updated);
      setCreditDialogOpen(false);
      setCreditAmount('');
      showToast(
        `Credit of $${amt.toFixed(2)} applied. Revised payment email will be sent on Validate Order.`,
        'success'
      );
    } catch (e) {
      console.error('Apply credit failed', e);
      showToast(`Failed to apply credit: ${e?.message || e}`, 'error');
    } finally {
      setApplyingCredit(false);
    }
  };

  // Handle radio selection - clear custom text and enable radio
  const handleRadioChange = (e) => {
    setCancelReason(e.target.value);
    setCustomCancelReason(''); // Clear custom text when radio is selected
  };

  // Handle custom text input - clear radio selection when user starts typing
  const handleCustomTextChange = (e) => {
    const value = e.target.value;
    setCustomCancelReason(value);
    // Clear radio selection as soon as user starts typing (even if empty)
    if (value !== '') {
      setCancelReason('');
    }
  };

  // Handle text field focus - clear radio selection when user clicks to type
  const handleCustomTextFocus = () => {
    if (cancelReason) {
      setCancelReason('');
    }
  };

  // Check if either radio or custom text is provided
  const hasValidReason = cancelReason || customCancelReason.trim();

  // Aesthetic timeline bar with completed ticks
  const TimelineBar = () => {
    const siteBlue = '#002F87';
    const inactive = '#c7cfdd';
    // Build timeline statuses:
    const timelineStatuses = useMemo(() => {
      if (status === 'CANCELLED') {
        // Show timeline up to the status before cancellation, then show CANCELLED
        const cutoffIdx = prevBeforeCancel ? BASE_STATUSES.indexOf(prevBeforeCancel) : 0;
        const base = cutoffIdx >= 0 ? BASE_STATUSES.slice(0, cutoffIdx + 1) : BASE_STATUSES.slice(0, 1);
        return [...base, 'CANCELLED'];
      }
      // Default: do not show CANCELLED in the running timeline
      return BASE_STATUSES;
    }, [status, prevBeforeCancel]);

    const currentIdx = Math.max(0, timelineStatuses.indexOf(status));

    return (
      <Box sx={{ px: 2, pt: 3, pb: 3 }}>
        <Stack direction="row" alignItems="flex-start" spacing={0} sx={{ width: '100%', position: 'relative' }}>
          {timelineStatuses.map((s, idx) => {
            const isCompleted = idx < currentIdx;
            const isCurrent = idx === currentIdx;
            const dotSize = 20;

            return (
              <React.Fragment key={`timeline-${s}`}>
                <Stack 
                  alignItems="center" 
                  sx={{ 
                    minWidth: { xs: 90, sm: 110 },
                    flex: 1,
                    position: 'relative',
                    zIndex: 2
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      mb: 2,
                      color: s === 'CANCELLED' && isCurrent ? '#dc3545' : (isCompleted || isCurrent ? siteBlue : '#4b5563'),
                      fontWeight: 600,
                      textAlign: 'center',
                      letterSpacing: 0.15,
                      fontSize: '0.7rem',
                      lineHeight: 1.3,
                      minHeight: '3.6em',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 0.5,
                      wordBreak: 'break-word',
                    }}
                  >
                    {s}
                  </Typography>
                  <Box
                    sx={{
                      position: 'relative',
                      width: dotSize,
                      height: dotSize,
                      borderRadius: '50%',
                      border: `3px solid ${s === 'CANCELLED' && isCurrent ? '#dc3545' : (isCompleted || isCurrent ? siteBlue : inactive)}`,
                      backgroundColor: s === 'CANCELLED' && isCurrent ? '#dc3545' : (isCompleted ? siteBlue : '#fff'),
                      transition: 'all 300ms ease',
                      boxShadow: isCurrent || isCompleted 
                        ? `0 0 0 4px ${s === 'CANCELLED' && isCurrent ? '#dc354520' : `${siteBlue}20`}` 
                        : 'none',
                      flexShrink: 0,
                    }}
                  >
                    {isCompleted && (
                      <CheckRoundedIcon
                        sx={{
                          color: '#fff',
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          fontSize: '0.875rem',
                        }}
                      />
                    )}
                    {isCurrent && !isCompleted && s !== 'CANCELLED' && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          width: 10,
                          height: 10,
                          bgcolor: siteBlue,
                          borderRadius: '50%',
                          transform: 'translate(-50%, -50%)',
                        }}
                      />
                    )}
                    {isCurrent && s === 'CANCELLED' && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          width: 10,
                          height: 10,
                          bgcolor: '#dc3545',
                          borderRadius: '50%',
                          transform: 'translate(-50%, -50%)',
                        }}
                      />
                    )}
                  </Box>
                </Stack>
                {idx < timelineStatuses.length - 1 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: `calc(3.6em + ${dotSize / 2}px)`,
                      transform: 'translateY(-50%)',
                      left: `calc(${(idx + 1) * (100 / timelineStatuses.length)}% - ${dotSize / 2}px)`,
                      right: `calc(${(timelineStatuses.length - idx - 1) * (100 / timelineStatuses.length)}% - ${dotSize / 2}px)`,
                      height: 2,
                      borderTop: isCompleted
                        ? `2px solid #9ca3af`
                        : `2px dotted ${inactive}`,
                      borderRadius: 0,
                      transition: 'border-color 300ms ease',
                      zIndex: 1,
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Stack>
      </Box>
    );
  };

  // Check if order is cancelled - grey out all sections
  const isCancelled = useMemo(() => {
    return (currentOrder?.status || order?.status || status) === 'CANCELLED';
  }, [currentOrder?.status, order?.status, status]);

  // Check if status is VENDOR VALIDATION REQUESTED or any status after it
  const isVendorConfirmedOrLater = useMemo(() => {
    const currentStatus = currentOrder?.status || order?.status || status;
    if (!currentStatus || currentStatus === 'CANCELLED') return false;
    const vendorValidationRequestedIdx = STATUS.indexOf('VENDOR VALIDATION REQUESTED');
    const currentStatusIdx = STATUS.indexOf(currentStatus);
    return currentStatusIdx >= vendorValidationRequestedIdx && currentStatusIdx >= 0;
  }, [currentOrder?.status, order?.status, status]);

  const SteppedContent = (
    <ContentContainer>
      <VerticalPaddingContainer>
        {/* Show cancellation message instead of timeline if cancelled */}
        {isCancelled ? (
          <Box sx={{ px: 2, pt: 3, pb: 3 }}>
            <Typography
              variant="h6"
              sx={{
                color: '#dc3545',
                fontWeight: 600,
                textAlign: 'center',
                py: 2,
              }}
            >
              Order Cancelled {cancellationReason ? `(Reason: ${cancellationReason})` : ''}
            </Typography>
          </Box>
        ) : (
          <TimelineBar />
        )}
        
        {/* Action Buttons Row */}
        {!readOnly && !isCancelled && effectiveOrderId && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, mb: 2, mt: 2 }}>
            <Button
              variant="outlined"
              color="error"
              size="medium"
              startIcon={<CancelIcon />}
              onClick={() => setCancelDialogOpen(true)}
              sx={{ fontSize: '0.875rem' }}
            >
              Cancel Order
            </Button>
          </Box>
        )}
        
        {/* Product Confirmation — always active */}
        <SectionCard status={isCancelled ? 'disabled' : 'active'} sx={isCancelled ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
          <SectionTitle>Product Confirmation</SectionTitle>

          <ProductConfirmation
            order={currentOrder || order}
            showSystemNote={false}
            editableActions={!readOnly}
            onOrderChanged={(updatedOrder) => {
              setCurrentOrder(updatedOrder);
              if (onOrderChanged) onOrderChanged(updatedOrder);
            }}
            isAdmin={!readOnly}
            showExcludedItems={true}
          />

          {!readOnly && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              System Note
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Select size="small" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ width: 180 }}>
                {STATUS.map((s) => {
                  const committed = order?.status || status;
                  const committedIdx = STATUS.indexOf(committed);
                  const idx = STATUS.indexOf(s);
                  const disableOption = s === committed || idx < committedIdx;
                  return (
                    <MenuItem key={s} value={s} disabled={disableOption}>{s}</MenuItem>
                  );
                })}
              </Select>

              <TextField
                size="small"
                fullWidth
                placeholder="Add internal note (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />

              <Button 
                variant="contained" 
                onClick={handleUpdate} 
                disabled={!hasChanges || updating}
              >
                {updating ? 'Updating...' : 'UPDATE'}
              </Button>
            </Stack>

            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              System note added by: {displayName}
            </Typography>

            <Divider sx={{ mt: 2 }} />
          </Box>
          )}

          {readOnly && canViewOrders && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Internal Notes
            </Typography>

            {(() => {
              const raw = (currentOrder || order)?.internal_notes || '';
              const cleaned = raw.replace(/VENDOR_SHIPPING_BREAKDOWN:\{.*\}/g, '').trim();
              return cleaned ? (
                <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: 'pre-wrap', bgcolor: 'grey.50', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {cleaned}
                </Typography>
              ) : null;
            })()}

            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                fullWidth
                placeholder="Add internal note..."
                value={internalNote}
                onChange={(e) => setInternalNote(e.target.value)}
              />
              <Button
                variant="contained"
                size="small"
                disabled={!internalNote.trim() || savingNote}
                onClick={async () => {
                  if (!internalNote.trim()) return;
                  setSavingNote(true);
                  try {
                    await addInternalNote(effectiveOrderId, internalNote.trim());
                    showToast('Note saved', 'success');
                    setInternalNote('');
                    const refreshed = await getOrderById(effectiveOrderId);
                    setCurrentOrder(refreshed);
                    if (onOrderChanged) onOrderChanged(refreshed);
                  } catch (e) {
                    showToast(e.message || 'Failed to save note', 'error');
                  } finally {
                    setSavingNote(false);
                  }
                }}
              >
                {savingNote ? 'Saving...' : 'Save Note'}
              </Button>
            </Stack>

            <Divider sx={{ mt: 2 }} />
          </Box>
          )}
        </SectionCard>

        {/* Order Summary — disabled only when cancelled */}
        {(() => {
          const isAwaitingPaymentOrLater = status === 'VENDOR VALIDATION REQUESTED' ||
                                          status === 'AWAITING PAYMENT' || 
                                          status === 'PAID' || 
                                          status === 'VENDOR ORDER PROCESSED' || 
                                          status === 'ASSEMBLING ORDER' || 
                                          status === 'SHIPPED' || 
                                          status === 'DELIVERED';
          // Only disable when cancelled
          const shouldDisable = isCancelled;
          return (
            <SectionCard 
              status={shouldDisable ? 'disabled' : 'active'} 
              sx={shouldDisable ? { opacity: 0.5, pointerEvents: 'none' } : {}}
            >
              <SectionTitle>Order Summary</SectionTitle>

              {/* totals are being passed via computedOrder */}
              <OrderSummary
                order={computedOrder}
                onComplete={handlePaymentLinkCreated}
                onOrderChanged={onOrderChanged}
                onBillableChange={handleBillableChange}
                disableButtons={readOnly || !isAwaitingPaymentOrLater}
                readOnly={readOnly}
              />

              {!readOnly && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, mt: 2 }}>
                {!isCancelled && (currentOrder?.status || order?.status) === 'AWAITING PAYMENT' && (
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<CreditCardIcon />}
                    onClick={() => {
                      const existing = num(currentOrder?.credit_amount || order?.credit_amount);
                      setCreditAmount(existing > 0 ? String(existing) : '');
                      setCreditDialogOpen(true);
                    }}
                  >
                    {num(currentOrder?.credit_amount || order?.credit_amount) > 0 ? 'Edit Credit' : 'Apply Credit'}
                  </Button>
                )}
                <Button
                  variant="contained"
                  onClick={handlePreviewOrder}
                  disabled={confirming || !effectiveOrderId}
                >
                  Preview Order
                </Button>
              </Box>
              )}

              {/* Order Preview Modal */}
              <OrderPreviewModal
                open={previewModalOpen}
                onClose={handleClosePreview}
                onConfirm={handleConfirmOrder}
                order={computedOrder}
                confirming={confirming}
                statusHistory={statusHistory}
                shipments={shipments}
              />
            </SectionCard>
          );
        })()}

        {/* Payment Confirmation — disabled only when cancelled */}
        <SectionCard 
          status={isCancelled ? 'disabled' : 'active'} 
          sx={isCancelled ? { opacity: 0.5, pointerEvents: 'none' } : {}}
        >
          <SectionTitle>Payment Confirmation</SectionTitle>
          <PaymentConfirmation order={computedOrder || currentOrder || order} onOrderChanged={onOrderChanged} />
        </SectionCard>

        {/* Documents — disabled only when cancelled */}
        <SectionCard 
          status={isCancelled ? 'disabled' : 'active'} 
          sx={isCancelled ? { opacity: 0.5, pointerEvents: 'none' } : {}}
        >
          <SectionTitle>Order Details & Documents</SectionTitle>
          <OrderDetailsDocuments order={order} />
        </SectionCard>

        {/* Uploaded Documents — Admin only, shows documents from SharePoint folders */}
        <SectionCard 
          status={isCancelled ? 'disabled' : 'active'} 
          sx={isCancelled ? { opacity: 0.5, pointerEvents: 'none' } : {}}
        >
          <SectionTitle>Uploaded Documents</SectionTitle>
          {order?.order_number && (
            <OrderDocumentsList orderNumber={order.order_number} />
          )}
        </SectionCard>

        {/* Apply / Edit Credit Dialog */}
        <Dialog open={creditDialogOpen} onClose={() => !applyingCredit && (setCreditDialogOpen(false), setCreditAmount(''))} maxWidth="xs" fullWidth>
          <DialogTitle>{num(currentOrder?.credit_amount || order?.credit_amount) > 0 ? 'Edit Credit' : 'Apply Credit'}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Apply a credit offset to this order. The revised payment email will be sent when you click Validate Order.
            </Typography>
            {(() => {
              const billableMax = round2(num(
                summaryBillable !== null && summaryBillable !== undefined
                  ? summaryBillable
                  : computedTotals?.billable
              ));
              const creditNum = parseFloat(creditAmount || 0) || 0;
              const remaining = round2(Math.max(0, billableMax - creditNum));
              return (
                <>
                  <Box sx={{ background: '#f5f5f5', borderRadius: 1, p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Billable</Typography>
                      <Typography variant="body2" fontWeight={600}>${billableMax.toFixed(2)}</Typography>
                    </Box>
                    {creditNum > 0 && (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ color: '#2e7d32' }}>Credit Applied</Typography>
                          <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>-${creditNum.toFixed(2)}</Typography>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" fontWeight={700}>Balance Due</Typography>
                          <Typography variant="body2" fontWeight={700}>${remaining.toFixed(2)}</Typography>
                        </Box>
                        {creditNum >= billableMax && (
                          <Typography variant="caption" sx={{ color: '#2e7d32', mt: 1, display: 'block' }}>
                            Order will be automatically marked as PAID on Validate Order
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                  <TextField
                    fullWidth
                    label="Credit Amount"
                    type="number"
                    value={creditAmount}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '') {
                        setCreditAmount('');
                        return;
                      }
                      const n = parseFloat(raw);
                      if (Number.isNaN(n)) return;
                      const clamped = Math.max(0, Math.min(n, billableMax));
                      setCreditAmount(String(clamped));
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      inputProps: { min: 0, max: billableMax, step: 0.01 }
                    }}
                    size="small"
                    helperText={`Max: $${billableMax.toFixed(2)} (Billable)`}
                  />
                </>
              );
            })()}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setCreditDialogOpen(false); setCreditAmount(''); }} disabled={applyingCredit}>
              Cancel
            </Button>
            <Button
              onClick={handleApplyCredit}
              variant="contained"
              color="success"
              disabled={!creditAmount || parseFloat(creditAmount) <= 0 || parseFloat(creditAmount) > round2(num(summaryBillable !== null && summaryBillable !== undefined ? summaryBillable : computedTotals?.billable)) || applyingCredit}
            >
              {applyingCredit ? 'Applying...' : (num(currentOrder?.credit_amount || order?.credit_amount) > 0 ? 'Update Credit' : 'Apply Credit')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cancel Order Dialog */}
        <Dialog open={cancelDialogOpen} onClose={() => !cancelling && (setCancelDialogOpen(false), setCancelReason(''), setCustomCancelReason(''))} maxWidth="sm" fullWidth>
          <DialogTitle>Cancel Order</DialogTitle>
          <DialogContent>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend" sx={{ mb: 2 }}>Please select a reason for cancellation:</FormLabel>
              <RadioGroup value={cancelReason} onChange={handleRadioChange}>
                {CANCELLATION_REASONS.map((reason) => (
                  <FormControlLabel key={reason} value={reason} control={<Radio />} label={reason} />
                ))}
              </RadioGroup>
            </FormControl>
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                Or enter a custom reason:
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Enter custom cancellation reason..."
                value={customCancelReason}
                onChange={handleCustomTextChange}
                onFocus={handleCustomTextFocus}
                sx={{ 
                  '& .MuiInputBase-root': {
                    backgroundColor: '#fff',
                  }
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setCancelDialogOpen(false);
                setCancelReason('');
                setCustomCancelReason('');
              }} 
              disabled={cancelling}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCancelOrder} 
              variant="contained" 
              color="error" 
              disabled={!hasValidReason || cancelling}
            >
              {cancelling ? 'Cancelling...' : 'Submit'}
            </Button>
          </DialogActions>
        </Dialog>
      </VerticalPaddingContainer>
    </ContentContainer>
  );

  if (embedded) return SteppedContent;

  return (
    <>
      <TopNavbar />
      <PageContainer>
        <AdminPortalSideBar />
        {SteppedContent}
      </PageContainer>
    </>
  );
}
